import { useEffect, useMemo, useRef, useState } from "react";
import { getStrapiMedia } from "../../utils/utils";

interface VideoSliderItemProps {
  videoUrl: string;
  mimeType: string;
  posterUrl?: string;
  isActive: boolean;
  priority?: boolean;
}

const VideoSliderItem = ({
  videoUrl,
  mimeType,
  posterUrl,
  isActive,
  priority = false,
}: VideoSliderItemProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const resolvedVideoSrc = useMemo(() => getStrapiMedia(videoUrl), [videoUrl]);
  const resolvedPosterSrc = useMemo(
    () => (posterUrl ? getStrapiMedia(posterUrl) : undefined),
    [posterUrl],
  );

  const shouldMountVideo = isActive || priority;

  useEffect(() => {
    setIsVideoReady(false);
  }, [resolvedVideoSrc, shouldMountVideo]);

  // Priority hero: keep poster as LCP — start fetching video only after window load.
  useEffect(() => {
    if (!priority || !shouldMountVideo) return;

    const video = videoRef.current;
    if (!video) return;

    const startVideoLoad = () => {
      if (video.preload === "none") {
        video.preload = "auto";
        video.load();
      }
    };

    if (document.readyState === "complete") {
      startVideoLoad();
    } else {
      window.addEventListener("load", startVideoLoad, { once: true });
    }
  }, [priority, shouldMountVideo, resolvedVideoSrc]);

  // Non-priority slides: Safari strictly honors preload="metadata" and never
  // buffers ahead, so `canplay` never fires there. Kick off the load manually
  // as soon as the slide becomes active and the video is mounted.
  useEffect(() => {
    if (priority || !shouldMountVideo) return;

    const video = videoRef.current;
    if (!video) return;

    if (video.preload !== "auto") {
      video.preload = "auto";
      video.load();
    }
  }, [priority, shouldMountVideo, resolvedVideoSrc]);

  useEffect(() => {
    if (!videoRef.current || !isVideoReady) return;

    const video = videoRef.current;

    if (isActive) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.warn("Video play failed:", error);
        });
      }
    } else {
      video.pause();
    }
  }, [isActive, isVideoReady]);

  return (
    <div className="slider__media">
      <div className="video-wrapper">
        {resolvedPosterSrc ? (
          <img
            className="video-poster"
            src={resolvedPosterSrc}
            alt=""
            fetchPriority={priority ? "high" : "low"}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
          />
        ) : (
          <div className="video-poster-placeholder" />
        )}
        {shouldMountVideo ? (
          <video
            ref={videoRef}
            loop
            muted
            playsInline
            preload={priority ? "none" : "metadata"}
            className={isVideoReady ? "ready" : ""}
            onCanPlay={() => setIsVideoReady(true)}
          >
            <source src={resolvedVideoSrc} type={mimeType} />
          </video>
        ) : null}
      </div>
    </div>
  );
};

export default VideoSliderItem;
